import i18n from "i18next";
import { Data, Info } from "react-spreadsheet-import/types/types";
import { EMAIL_REGEX, NAME_REGEX } from "./constants";
import {
  cleanPhoneNumber,
  validatePhoneNumber,
  isNumberMissingCountryCode,
  addNorwegianCountryCodeToNorwegianNumbers,
  capitalizeName,
} from "./utils";

const t = i18n.t;

export const getBulkUploadTranslations = (isPrereg: boolean) => ({
  uploadStep: {
    title: t(
      isPrereg
        ? "PREREGISTRATION.BULK_UPLOAD_HEADER"
        : "USERS.BULK_UPLOAD.HEADER",
    ),
    manifestTitle: t(
      isPrereg
        ? "PREREGISTRATION.BULK_UPLOAD_DESCRIPTION"
        : "USERS.BULK_UPLOAD.DESCRIPTION",
    ),
    manifestDescription: t("USERS.BULK_UPLOAD.DESCRIPTION_SMALL"),
    dropzone: {
      title: t("USERS.BULK_UPLOAD.DROPZONE_TITLE"),
      buttonTitle: t("USERS.BULK_UPLOAD.DROPZONE_BUTTON"),
    },
  },
  selectHeaderStep: {
    title: t("USERS.BULK_UPLOAD.SELECT_HEADER_TITLE"),
    nextButtonTitle: t("USERS.BULK_UPLOAD.NEXT_BUTTON"),
  },
  matchColumnsStep: {
    title: t("USERS.BULK_UPLOAD.MATCH_COLUMNS_TITLE"),
    nextButtonTitle: t("USERS.BULK_UPLOAD.NEXT_BUTTON"),
    userTableTitle: t("USERS.BULK_UPLOAD.USER_TABLE_TITLE"),
    templateTitle: t("USERS.BULK_UPLOAD.TEMPLATE_TITLE"),
    selectPlaceholder: t("USERS.BULK_UPLOAD.SELECT_PLACEHOLDER"),
    ignoredColumnText: t("USERS.BULK_UPLOAD.IGNORED_COLUMN_TEXT"),
    duplicateColumnWarningTitle: t(
      "USERS.BULK_UPLOAD.DUPLICATE_COLUMN_WARNING_TITLE",
    ),
    duplicateColumnWarningDescription: t(
      "USERS.BULK_UPLOAD.DUPLICATE_COLUMN_WARNING_DESCRIPTION",
    ),
  },
  validationStep: {
    title: t("USERS.BULK_UPLOAD.VALIDATION_TITLE"),
    nextButtonTitle: t("USERS.CONFIRM"),
    noRowsMessage: t("USERS.BULK_UPLOAD.NO_ROWS_MESSAGE"),
    noRowsMessageWhenFiltered: t(
      "USERS.BULK_UPLOAD.NO_ROWS_MESSAGE_WHEN_FILTERED",
    ),
    discardButtonTitle: t("USERS.BULK_UPLOAD.DISCARD_BUTTON"),
    filterSwitchTitle: t("USERS.BULK_UPLOAD.FILTER_SWITCH"),
  },
  alerts: {
    unmatchedRequiredFields: {
      headerTitle: t("USERS.BULK_UPLOAD.UNMATCHED_REQUIRED_FIELDS_HEADER"),
      bodyText: t("USERS.BULK_UPLOAD.UNMATCHED_REQUIRED_FIELDS_BODY"),
      listTitle: t("USERS.BULK_UPLOAD.UNMATCHED_REQUIRED_FIELDS_LIST_TITLE"),
      cancelButtonTitle: t("CANCEL"),
      continueButtonTitle: t("CONTINUE"),
    },
    submitIncomplete: {
      headerTitle: t("USERS.BULK_UPLOAD.SUBMIT_INCOMPLETE_HEADER"),
      bodyText: t("USERS.BULK_UPLOAD.SUBMIT_INCOMPLETE_BODY"),
      cancelButtonTitle: t("CANCEL"),
      finishButtonTitle: t("SUBMIT"),
    },
    confirmClose: {
      headerTitle: t("USERS.BULK_UPLOAD.CONFIRM_CLOSE_HEADER"),
      bodyText: t("USERS.BULK_UPLOAD.CONFIRM_CLOSE_BODY"),
      cancelButtonTitle: t("CANCEL"),
      exitButtonTitle: t("EXIT"),
    },
    submitError: {
      title: t("USERS.BULK_UPLOAD.SUBMIT_ERROR_TITLE"),
      defaultMessage: t("USERS.BULK_UPLOAD.SUBMIT_ERROR_DEFAULT_MESSAGE"),
    },
  },
});

export const validateFirstName = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  if (data.firstName && typeof data.firstName === "string") {
    if (data.firstName.length < 2) {
      addError("firstName", {
        message: t("VALIDATION.MIN_LENGTH", {
          field: t("ADD_USER.FIRST_NAME"),
          length: 2,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }

    if (!NAME_REGEX.test(data.firstName)) {
      addError("firstName", {
        message: t("VALIDATION.FORBIDDEN_CHARACTERS"),
        level: "error",
      });
    }

    if (data.firstName.length > 30) {
      addError("firstName", {
        message: t("VALIDATION.MAX_LENGTH", {
          field: t("ADD_USER.FIRST_NAME"),
          length: 30,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }
  }
};

export const validateFullNamePrereg = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  if (data.nameBulkUpload && typeof data.nameBulkUpload === "string") {
    if (data.nameBulkUpload.length < 3) {
      addError("nameBulkUpload", {
        message: t("VALIDATION.MIN_LENGTH", {
          field: t("USERS.TABLE_HEADERS.NAME"),
          length: 3,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }

    if (!NAME_REGEX.test(data.nameBulkUpload)) {
      addError("nameBulkUpload", {
        message: t("VALIDATION.FORBIDDEN_CHARACTERS"),
        level: "error",
      });
    }

    if (data.nameBulkUpload.length > 30) {
      addError("nameBulkUpload", {
        message: t("VALIDATION.MAX_LENGTH", {
          field: t("USERS.TABLE_HEADERS.NAME"),
          length: 30,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }
  }
};

export const BULK_UPLOAD_THEME = {
  colors: {
    textColor: "var(--wlcm-color-white)",
    subtitleColor: "var(--wlcm-color-light-grey)",
    inactiveColor: "var(--wlcm-color-danger-red)",

    border: "var(--wlcm-color-grey-border)",
    background: "var(--wlcm-color-dark-grey)",
    backgroundAlpha: "rgba(255,255,255,0)",
    secondaryBackground: "var(--wlcm-color-light-black)",
    highlight: "var(--wlcm-color-grey-border)",
    rsi: {
      50: "var(--wlcm-color-grey-border)",
      100: "var(--wlcm-color-dark-inactive-grey)",
      500: "var(--wlcm-color-blue)",
      600: "#2d5fe2",
    },
    green: {
      500: "var(--wlcm-color-success-green)",
    },
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: "22px;",
      },
    },
  },
} as const;

export const validateCustomEmployeeNumber = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  const value = data.customEmployeeId;
  if (value && typeof value === "string") {
    if (value.length > 15) {
      addError("customEmployeeId", {
        message: t("VALIDATION.MAX_LENGTH", {
          field: t("USERS.TABLE_HEADERS.CUSTOM_EMPLOYEE_ID"),
          length: 15,
          type: t("NUMBERS"),
        }),
        level: "error",
      });
    }
    if (!/^\d+$/.test(value)) {
      addError("customEmployeeId", {
        message: t("VALIDATION.NUMBER", {
          field: t("USERS.TABLE_HEADERS.CUSTOM_EMPLOYEE_ID"),
        }),
        level: "error",
      });
    }
  }
};

export const validateJobTitle = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  const value = data.jobTitle;
  if (value && typeof value === "string") {
    // Allow Unicode letters, numbers, spaces, and common job title punctuation (including Norwegian ø, å, æ)
    if (!/^[\p{L}\p{N}\s.,\-()&']+$/u.test(value)) {
      addError("jobTitle", {
        message: t("VALIDATION.FORBIDDEN_CHARACTERS"),
        level: "error",
      });
    }
    if (value.length > 50) {
      addError("jobTitle", {
        message: t("VALIDATION.MAX_LENGTH", {
          field: t("ADD_USER.JOB_TITLE"),
          length: 50,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }
  }
};

export const validateLastName = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  if (data.lastName && typeof data.lastName === "string") {
    if (data.lastName.length < 2) {
      addError("lastName", {
        message: t("VALIDATION.MIN_LENGTH", {
          field: t("ADD_USER.LAST_NAME"),
          length: 2,
          type: t("CHARACTERS"),
        }),
        level: "error",
      });
    }

    if (!NAME_REGEX.test(data.lastName)) {
      addError("lastName", {
        message: t("VALIDATION.FORBIDDEN_CHARACTERS"),
        level: "error",
      });
    }

    if (data.lastName.length > 30) {
      addError("lastName", {
        message: t("VALIDATION.MAX_LENGTH", {
          field: t("ADD_USER.LAST_NAME"),
          length: 30,
        }),
        level: "error",
      });
    }
  }
};

export const validateEmail = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
) => {
  if (data.email && typeof data.email === "string") {
    data.email = data.email.toLowerCase();

    if (!EMAIL_REGEX.test(data.email)) {
      addError("email", {
        message: t("VALIDATION.INVALID", {
          field: t("ADD_USER.EMAIL"),
        }),
        level: "error",
      });
    }
  }
};

export const validatePhone = (
  data: Data<string>,
  addError: (fieldKey: string, error: Info) => void,
  fieldName: string,
) => {
  if (
    data[fieldName] &&
    typeof data[fieldName] === "string" &&
    data[fieldName] !== ""
  ) {
    const cleanedNumber = cleanPhoneNumber(data[fieldName]);
    const isValidPhone = validatePhoneNumber(cleanedNumber);

    if (!isValidPhone) {
      addError(fieldName, {
        message: t("VALIDATION.INVALID_PHONE"),
        level: "error",
      });
    }

    const isMissingCountryCode = isNumberMissingCountryCode(cleanedNumber);
    if (isMissingCountryCode) {
      addError(data[fieldName], {
        message: t("VALIDATION.CONTRY_CODE_MISSING"),
        level: "error",
      });
    }

    const finalPhoneNumber =
      addNorwegianCountryCodeToNorwegianNumbers(cleanedNumber);
    return { ...data, [fieldName]: finalPhoneNumber };
  }

  return data;
};

// Built at call time: in prod bundles this module can be evaluated before i18n is initialised
export const getBulkUploadFieldsPrereg = () => [
  {
    label: t("USERS.TABLE_HEADERS.NAME"),
    key: "nameBulkUpload",
    alternateMatches: ["name", "full name", "navn", "fullt navn"],
    fieldType: {
      type: "input",
    },
    example: "Ola Normann",
    validations: [
      {
        rule: "required",
        errorMessage: t("VALIDATION.REQUIRED", {
          field: t("USERS.TABLE_HEADERS.NAME"),
        }),
        level: "error",
      },
    ],
  },
  {
    label: `${t("USERS.TABLE_HEADERS.PHONE")} ${t("USERS.BULK_UPLOAD.OPTIONAL")}`,
    key: "phoneBulkUpload",
    alternateMatches: [
      "phone number",
      "phone",
      "telephone",
      "telefon",
      "telefonnummer",
    ],
    fieldType: {
      type: "input",
    },
    example: "+4798765432",
    validations: [
      {
        rule: "unique",
        errorMessage: t("VALIDATION.DUPLICATE_PHONE"),
        level: "error",
        allowEmpty: true,
      },
    ],
  },
  {
    label: `${t("USERS.LANGUAGE_PREFERENCE")} ${t("USERS.BULK_UPLOAD.OPTIONAL")}`,
    key: "languageBulkUpload",
    alternateMatches: [
      "language",
      "språk",
      "lang",
      "sprak",
      "Language",
      "Språk",
      "Lang",
      "Sprak",
    ],
    fieldType: {
      type: "select",
      options: [
        { label: t("LANG_EN"), value: "en" },
        { label: t("LANG_NO"), value: "no" },
      ],
    },
    example: "en",
  },
];

export const normalizeFirstName = (data: Data<string>) => {
  if (data.firstName && typeof data.firstName === "string") {
    return { ...data, firstName: capitalizeName(data.firstName) };
  }
  return data;
};

export const normalizeLastName = (data: Data<string>) => {
  if (data.lastName && typeof data.lastName === "string") {
    return { ...data, lastName: capitalizeName(data.lastName) };
  }
  return data;
};
